import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  MapPin,
  Phone,
  Mail,
  User,
  Bed,
  Clock,
  CreditCard,
  FileText,
  AlertCircle,
  X,
} from "lucide-react";
import bookingService, {
  Booking,
  BookingAuditLog,
} from "../../services/bookingService";

const BookingDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [auditLogs, setAuditLogs] = useState<BookingAuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChangeSession, setSelectedChangeSession] = useState<
    BookingAuditLog[] | null
  >(null);
  const [showChangeModal, setShowChangeModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBookingDetails();
    }
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const [bookingResponse, auditResponse] = await Promise.all([
        bookingService.getBookingById(parseInt(id!)),
        bookingService.getBookingAuditLogs(parseInt(id!)),
      ]);
      setBooking(bookingResponse.data);
      setAuditLogs(auditResponse.data);
    } catch (err) {
      setError("Failed to fetch booking details");
      console.error("Error fetching booking details:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Group audit logs by change session (same timestamp and user)
  const groupAuditLogsBySession = (logs: BookingAuditLog[]) => {
    const grouped: { [key: string]: BookingAuditLog[] } = {};

    logs.forEach((log) => {
      const sessionKey = `${log.created_at}_${log.changed_by}`;
      if (!grouped[sessionKey]) {
        grouped[sessionKey] = [];
      }
      grouped[sessionKey].push(log);
    });

    return Object.values(grouped);
  };

  const handleChangeSessionClick = (changeSession: BookingAuditLog[]) => {
    setSelectedChangeSession(changeSession);
    setShowChangeModal(true);
  };

  const formatIndianCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format values for display in audit logs
  const formatAuditValue = (value: string | null, fieldName: string) => {
    if (!value || value === "null" || value === "undefined") {
      return "N/A";
    }

    // Format amounts with proper decimal places
    if (fieldName.includes("amount") || fieldName.includes("charge")) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        return numValue.toFixed(2);
      }
    }

    // Format dates to show only date part (DD/MM/YY)
    if (
      fieldName.includes("date") &&
      (fieldName.includes("checkin") || fieldName.includes("checkout"))
    ) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const day = date.getDate().toString().padStart(2, "0");
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const year = date.getFullYear().toString().slice(-2);
          return `${day}/${month}/${year}`;
        }
      } catch (error) {
        // If date parsing fails, return original value
      }
    }

    return value;
  };

  // Filter out unchanged audit logs
  const getActualChanges = (changeSession: BookingAuditLog[]) => {
    return changeSession.filter((log) => {
      const oldValue = formatAuditValue(log.old_value, log.field_name);
      const newValue = formatAuditValue(log.new_value, log.field_name);
      return oldValue !== newValue;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "booked (HCN Pending)":
        return "bg-sky-100 text-sky-800";
      case "pending (HBR Pending)":
        return "bg-amber-100 text-amber-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "no_show":
        return "bg-gray-100 text-gray-800";
      case "checked_in":
        return "bg-blue-100 text-blue-800";
      case "checked_out":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "refunded":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-white">Loading booking details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-950 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-red-400">{error || "Booking not found"}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate("/bookings")}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Bookings
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Booking Details</h1>
              <p className="text-gray-400">{booking.booking_reference}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() =>
                booking?.booking_status !== "cancelled" &&
                navigate(`/bookings/${id}/edit`)
              }
              className={`${
                booking?.booking_status === "cancelled"
                  ? "border-gray-600 text-gray-500 bg-gray-600/10 cursor-not-allowed"
                  : "border-green-600 text-green-400 bg-green-600/10 hover:bg-green-600 hover:text-white"
              }`}
              disabled={booking?.booking_status === "cancelled"}
              title={
                booking?.booking_status === "cancelled"
                  ? "Cannot edit cancelled bookings"
                  : "Edit booking"
              }
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              className="border-red-600 text-red-400 bg-red-600/10 hover:bg-red-600 hover:text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-600/20 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Booking Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      booking.booking_status
                    )}`}
                  >
                    {booking.booking_status}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-600/20 rounded-lg">
                  <CreditCard className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Payment Status</p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(
                      booking.payment_status
                    )}`}
                  >
                    {booking.payment_status}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-600/20 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Total Amount</p>
                  <p className="text-lg font-semibold text-white">
                    ₹{formatIndianCurrency(booking.total_amount)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Guest Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <User className="w-5 h-5 mr-2" />
                Guest Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Full Name</label>
                  <p className="text-white font-medium">
                    {booking.guest_title && `${booking.guest_title} `}
                    {booking.guest_first_name && `${booking.guest_first_name} `}
                    {booking.guest_last_name && `${booking.guest_last_name}`}
                    {!booking.guest_first_name &&
                      !booking.guest_last_name &&
                      booking.guest_name}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Email</label>
                  <p className="text-white">{booking.guest_email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Phone</label>
                  <p className="text-white">
                    {booking.guest_phone || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Nationality</label>
                  <p className="text-white">
                    {booking.guest_nationality || "Not provided"}
                  </p>
                </div>
              </div>
              {booking.guest_address && (
                <div>
                  <label className="text-sm text-gray-400">Address</label>
                  <p className="text-white">{booking.guest_address}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Guest List */}
          {booking.guests && booking.guests.length > 0 && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Guest List ({booking.guests.length}{" "}
                  {booking.guests.length === 1 ? "Guest" : "Guests"})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {booking.guests.map((guest, index) => (
                    <div
                      key={guest.id}
                      className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              guest.guest_type === "AD"
                                ? "bg-blue-600/20 text-blue-400"
                                : "bg-green-600/20 text-green-400"
                            }`}
                          >
                            {guest.guest_type === "AD" ? "Adult" : "Child"}
                          </span>
                          <span className="text-white font-medium">
                            {guest.first_name} {guest.surname}
                          </span>
                        </div>
                        {guest.age && (
                          <span className="text-gray-400 text-sm">
                            Age: {guest.age}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {guest.passport_number && (
                          <div>
                            <label className="text-gray-400">Passport</label>
                            <p className="text-white">
                              {guest.passport_number}
                            </p>
                          </div>
                        )}
                        {guest.nationality && (
                          <div>
                            <label className="text-gray-400">Nationality</label>
                            <p className="text-white">{guest.nationality}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hotel Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Hotel Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-gray-400">Hotel Name</label>
                <p className="text-white font-medium">{booking.hotel_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">City</label>
                  <p className="text-white">{booking.city}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Country</label>
                  <p className="text-white">{booking.country}</p>
                </div>
              </div>
              {booking.street_address && (
                <div>
                  <label className="text-sm text-gray-400">Address</label>
                  <p className="text-white">{booking.street_address}</p>
                </div>
              )}
              {booking.hotel_phone && (
                <div>
                  <label className="text-sm text-gray-400">Hotel Phone</label>
                  <p className="text-white">{booking.hotel_phone}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Details */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Booking Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Check-in Date</label>
                  <p className="text-white font-medium">
                    {formatDate(booking.checkin_date)}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">
                    Check-out Date
                  </label>
                  <p className="text-white font-medium">
                    {formatDate(booking.checkout_date)}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Nights</label>
                  <p className="text-white">{booking.nights_count || "N/A"}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Rooms</label>
                  <p className="text-white">
                    {booking.rooms_count || booking.rooms}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Adults</label>
                  <p className="text-white">
                    {booking.adults_count || booking.adults}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Children</label>
                  <p className="text-white">
                    {booking.children_count || booking.children}
                  </p>
                </div>
              </div>
              {booking.checkin_time && (
                <div>
                  <label className="text-sm text-gray-400">Check-in Time</label>
                  <p className="text-white">{booking.checkin_time}</p>
                </div>
              )}
              {booking.checkout_time && (
                <div>
                  <label className="text-sm text-gray-400">
                    Check-out Time
                  </label>
                  <p className="text-white">{booking.checkout_time}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Room Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Bed className="w-5 h-5 mr-2" />
                Room Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-gray-400">Room Type</label>
                <p className="text-white font-medium">
                  {booking.room_type_name}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Plan Name</label>
                <p className="text-white">{booking.plan_name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Plan Code</label>
                <p className="text-white">{booking.plan_code}</p>
              </div>
              {booking.meal_plan && (
                <div>
                  <label className="text-sm text-gray-400">Meal Plan</label>
                  <p className="text-white">{booking.meal_plan}</p>
                </div>
              )}
              {booking.room_description && (
                <div>
                  <label className="text-sm text-gray-400">Description</label>
                  <p className="text-white">{booking.room_description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <CreditCard className="w-5 h-5 mr-2" />
                Financial Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {booking.base_amount && (
                  <div>
                    <label className="text-sm text-gray-400">Base Amount</label>
                    <p className="text-white">
                      ₹{formatIndianCurrency(booking.base_amount)}
                    </p>
                  </div>
                )}
                {booking.markup_amount && (
                  <div>
                    <label className="text-sm text-gray-400">
                      Markup Amount
                    </label>
                    <p className="text-white">
                      ₹{formatIndianCurrency(booking.markup_amount)}
                    </p>
                  </div>
                )}
                {booking.service_charge_amount && (
                  <div>
                    <label className="text-sm text-gray-400">
                      Service Charge
                    </label>
                    <p className="text-white">
                      ₹{formatIndianCurrency(booking.service_charge_amount)}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-400">Total Amount</label>
                  <p className="text-white font-semibold">
                    ₹{formatIndianCurrency(booking.total_amount)}
                  </p>
                </div>
              </div>
              {booking.pending_amount && (
                <div>
                  <label className="text-sm text-gray-400">
                    Pending Amount
                  </label>
                  <p className="text-white">
                    ₹{formatIndianCurrency(booking.pending_amount)}
                  </p>
                </div>
              )}
              {booking.refund_amount && (
                <div>
                  <label className="text-sm text-gray-400">Refund Amount</label>
                  <p className="text-white">
                    ₹{formatIndianCurrency(booking.refund_amount)}
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-400">Currency</label>
                <p className="text-white">{booking.currency}</p>
              </div>
            </CardContent>
          </Card>

          {/* Cancellation Information */}
          {booking.original_cancellation_policy && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Cancellation Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Original Cancellation Policy */}
                <div className="bg-gray-800 p-4 rounded-lg">
                  <h4 className="text-white font-medium mb-2">
                    Original Cancellation Policy
                  </h4>
                  <p className="text-gray-300 text-sm mb-2">
                    {booking.original_cancellation_policy.text}
                  </p>
                  {/* Additional Cancellation Details */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">
                        Refundable at Booking:
                      </span>
                      <span className="text-white ml-2">
                        {booking.is_refundable ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Cancellation Fee:</span>
                      <span className="text-white ml-2">
                        ₹
                        {formatIndianCurrency(
                          parseFloat(
                            booking.original_cancellation_policy.amount
                          )
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Deadline:</span>
                      <span className="text-white ml-2">
                        {formatDateTime(
                          booking.original_cancellation_policy.deadline
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Current Cancellation Status */}
                <div
                  className={`p-4 rounded-lg ${
                    booking.current_cancellation_status === "refundable"
                      ? "bg-green-900/30 border border-green-500/30"
                      : booking.current_cancellation_status === "non_refundable"
                      ? "bg-red-900/30 border border-red-500/30"
                      : "bg-yellow-900/30 border border-yellow-500/30"
                  }`}
                >
                  <h4 className="text-white font-medium mb-2 flex items-center">
                    <span
                      className={`w-2 h-2 rounded-full mr-2 ${
                        booking.current_cancellation_status === "refundable"
                          ? "bg-green-500"
                          : booking.current_cancellation_status ===
                            "non_refundable"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    ></span>
                    Current Cancellation Status
                  </h4>
                  <p
                    className={`text-sm font-medium ${
                      booking.current_cancellation_status === "refundable"
                        ? "text-green-300"
                        : booking.current_cancellation_status ===
                          "non_refundable"
                        ? "text-red-300"
                        : "text-yellow-300"
                    }`}
                  >
                    {booking.current_cancellation_status === "refundable"
                      ? "Refundable"
                      : booking.current_cancellation_status === "non_refundable"
                      ? "Non-Refundable"
                      : "Unknown Status"}
                  </p>
                  <p className="text-gray-300 text-sm mt-1">
                    {booking.current_cancellation_message}
                  </p>

                  {/* Time Remaining */}
                  {booking.time_remaining_hours &&
                    booking.time_remaining_hours > 0 && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-400">Time Remaining:</span>
                        <span className="text-white ml-2">
                          {booking.time_remaining_hours > 24
                            ? `${Math.floor(
                                booking.time_remaining_hours / 24
                              )} days, ${
                                booking.time_remaining_hours % 24
                              } hours`
                            : `${booking.time_remaining_hours} hours`}
                        </span>
                      </div>
                    )}
                </div>

                {/* Cancellation History (if booking was cancelled) */}
                {(booking.cancellation_datetime ||
                  booking.cancellation_reason ||
                  booking.cancellation_charge) && (
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h4 className="text-white font-medium mb-2">
                      Cancellation History
                    </h4>
                    <div className="space-y-2 text-sm">
                      {booking.cancellation_datetime && (
                        <div>
                          <span className="text-gray-400">Cancelled On:</span>
                          <span className="text-white ml-2">
                            {formatDateTime(booking.cancellation_datetime)}
                          </span>
                        </div>
                      )}
                      {booking.cancellation_reason && (
                        <div>
                          <span className="text-gray-400">
                            Cancellation Reason:
                          </span>
                          <span className="text-white ml-2">
                            {booking.cancellation_reason}
                          </span>
                        </div>
                      )}
                      {booking.cancellation_charge && (
                        <div>
                          <span className="text-gray-400">
                            Cancellation Charge:
                          </span>
                          <span className="text-white ml-2">
                            ₹{formatIndianCurrency(booking.cancellation_charge)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Booking Information */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Booking Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {booking.booking_reference && (
                  <div>
                    <label className="text-sm text-gray-400">
                      Booking Reference
                    </label>
                    <p className="text-white font-mono text-sm">
                      {booking.booking_reference}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-400">
                    Hotel Booking Reference
                  </label>
                  <p className="text-white">
                    {booking.hotel_booking_ref_number || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">
                    Hotel Confirmation Number
                  </label>
                  <p className="text-white">
                    {booking.hotel_confirmation_number || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">
                    Client Reference
                  </label>
                  <p className="text-white">
                    {booking.client_reference || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">
                    Supplier Reference
                  </label>
                  <p className="text-white">
                    {booking.supplier_reference || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">
                    Booking Source
                  </label>
                  <p className="text-white">{booking.booking_source}</p>
                </div>
                {booking.booking_channel && (
                  <div>
                    <label className="text-sm text-gray-400">
                      Booking Channel
                    </label>
                    <p className="text-white">{booking.booking_channel}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-400">Refundable</label>
                  <p className="text-white">
                    {booking.is_refundable ? "Yes" : "No"}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400">Booking Date</label>
                <p className="text-white">
                  {booking.booking_datetime
                    ? formatDateTime(booking.booking_datetime)
                    : formatDateTime(booking.created_at)}
                </p>
              </div>
              {booking.confirmation_datetime && (
                <div>
                  <label className="text-sm text-gray-400">
                    Confirmation Date
                  </label>
                  <p className="text-white">
                    {formatDateTime(booking.confirmation_datetime)}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Special Requests & Remarks */}
          {(booking.special_requests || booking.internal_remarks) && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {booking.special_requests && (
                  <div>
                    <label className="text-sm text-gray-400">
                      Special Requests
                    </label>
                    <p className="text-white">{booking.special_requests}</p>
                  </div>
                )}
                {booking.internal_remarks && (
                  <div>
                    <label className="text-sm text-gray-400">
                      Internal Remarks
                    </label>
                    <p className="text-white">{booking.internal_remarks}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Audit Logs */}
          {auditLogs.length > 0 && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Change History ({
                    groupAuditLogsBySession(auditLogs).length
                  }{" "}
                  {groupAuditLogsBySession(auditLogs).length === 1
                    ? "Change Session"
                    : "Change Sessions"}
                  )
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {groupAuditLogsBySession(auditLogs).map(
                    (changeSession, index) => (
                      <div
                        key={index}
                        onClick={() => handleChangeSessionClick(changeSession)}
                        className="bg-gray-800 rounded-lg p-4 border border-gray-700 cursor-pointer hover:bg-gray-750 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-medium">
                              {getActualChanges(changeSession).length}{" "}
                              {getActualChanges(changeSession).length === 1
                                ? "field changed"
                                : "fields changed"}
                            </span>
                            <span className="text-gray-400 text-sm">
                              by {changeSession[0].changed_by_name}
                            </span>
                          </div>
                          <span className="text-gray-400 text-sm">
                            {formatDateTime(changeSession[0].created_at)}
                          </span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Change Details Modal */}
      {showChangeModal && selectedChangeSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">
                Change Details
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChangeModal(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
                  <span>
                    Changed by: {selectedChangeSession[0].changed_by_name}
                  </span>
                  <span>
                    {formatDateTime(selectedChangeSession[0].created_at)}
                  </span>
                </div>
                {selectedChangeSession[0].change_reason && (
                  <div className="bg-gray-800 rounded-lg p-3 mb-4">
                    <label className="text-gray-400 text-sm">Reason:</label>
                    <p className="text-gray-300 text-sm mt-1">
                      {selectedChangeSession[0].change_reason}
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {getActualChanges(selectedChangeSession).map((log) => (
                  <div
                    key={log.id}
                    className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                  >
                    <div className="mb-3">
                      <span className="text-white font-medium capitalize">
                        {log.field_name.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="text-gray-400">From:</label>
                        <p className="text-white">
                          {formatAuditValue(log.old_value, log.field_name)}
                        </p>
                      </div>
                      <div>
                        <label className="text-gray-400">To:</label>
                        <p className="text-white">
                          {formatAuditValue(log.new_value, log.field_name)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetails;
